import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, Button, Input, Table, Badge } from '@/components/ui';
import { Plus, Search, FileText, Trash2, ExternalLink, Eye } from 'lucide-react';
import { format } from 'date-fns';

interface Page {
  id: string;
  slug: string;
  title: string;
  titleTr?: string;
  metaDescription?: string;
  isPublished: boolean;
  publishedAt?: string;
  updatedAt: string;
}

export function PagesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: pages, isLoading } = useQuery<Page[]>({
    queryKey: ['pages', { search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const response = await api.get(`/admin/pages?${params}`);
      return response.data?.data?.items || response.data?.data || [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/pages/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  const columns = [
    {
      key: 'icon',
      header: '',
      className: 'w-12',
      render: () => (
        <div className="w-8 h-8 rounded bg-midnight-100 flex items-center justify-center">
          <FileText className="h-4 w-4 text-gray-500" />
        </div>
      ),
    },
    {
      key: 'title',
      header: 'Page',
      render: (page: Page) => (
        <div>
          <p className="font-medium text-white">{page.title}</p>
          <p className="text-sm text-gray-500">/{page.slug}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (page: Page) => (
        <Badge variant={page.isPublished ? 'success' : 'default'}>
          {page.isPublished ? 'Published' : 'Draft'}
        </Badge>
      ),
    },
    {
      key: 'updated',
      header: 'Last Updated',
      render: (page: Page) => (
        <span className="text-gray-400">
          {format(new Date(page.updatedAt), 'MMM d, yyyy')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-32',
      render: (page: Page) => (
        <div className="flex items-center gap-2">
          {page.isPublished && (
            <a
              href={`/${page.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-midnight-100"
              onClick={(e) => e.stopPropagation()}
            >
              <Eye className="h-4 w-4" />
            </a>
          )}
          <Link
            to={`/pages/${page.id}`}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-midnight-100"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-4 w-4" />
          </Link>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (confirm('Delete this page?')) {
                deleteMutation.mutate(page.id);
              }
            }}
            className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-midnight-100"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Pages</h1>
          <p className="text-gray-400 mt-1">Manage website content pages</p>
        </div>
        <Link to="/pages/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Page
          </Button>
        </Link>
      </div>

      {/* Search */}
      <Card padding="md">
        <Input
          placeholder="Search pages..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<Search className="h-4 w-4" />}
        />
      </Card>

      {/* Pages Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={pages ?? []}
          keyField="id"
          loading={isLoading}
          onRowClick={(page) => navigate(`/pages/${page.id}`)}
          emptyMessage="No pages found"
        />
      </Card>
    </div>
  );
}
